import React, { FC, useCallback, useEffect, useState } from 'react';
import { useUserContext } from '../../../providers/UserProvider';
import { Prompt } from '../../shared/Prompt';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './AccountReviewPrompt.scss';
import { useUserProfileUpdateMutation } from '../../../libs/graphql/baseAppAPI/mutations/__generated__/user-profile-update.mutation.generated';
import { APP_URLS, xTenantId } from '../../../consts';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../shared/Button';
import { useSnackbar } from '../../../hooks/snackbar';

export const AccountReviewPrompt: FC = () => {
  const { consents, fetchUserProfile, userProfileId } = useUserContext();
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [showPrompt, setShowPrompt] = useState(false);
  const location = useLocation();
  const { addSnack } = useSnackbar();

  const [userProfileUpdateMutation] = useUserProfileUpdateMutation({
    onCompleted: async (data) => {
      if (!data) return;
      await fetchUserProfile();
      setShowPrompt(false);
    },
    onError: (error) => {
      console.error(error);

      return handleError();
    },
  });

  const handleError = (): void => {
    addSnack({
      type: 'error',
      theme: 'dark',
      message: `Oops, something's gone wrong`,
    });
    navigate(APP_URLS.home);

    return;
  };

  useEffect(() => {
    if (!consents) {
      return;
    }

    const { lastAccountReviewOnUtc: lastReview, lastAccountReviewInterval: interval } = consents;

    if (!lastReview || !interval) {
      return;
    }

    const reviewDueDate = new Date(lastReview);

    if (interval === 'P1M') {
      reviewDueDate.setMonth(reviewDueDate.getMonth() + 1);
    } else if (interval === 'P3M') {
      reviewDueDate.setMonth(reviewDueDate.getMonth() + 3);
    } else if (interval === 'P6M') {
      reviewDueDate.setMonth(reviewDueDate.getMonth() + 6);
    }

    const currentDate = new Date();
    const isPastDue = currentDate > reviewDueDate;

    setShowPrompt(isPastDue);
  }, [consents]);

  const handleAcknowledge = async (): Promise<void> => {
    const currentDate = new Date().toISOString();

    await userProfileUpdateMutation({
      variables: {
        userProfileId,
        xTenantId,
        userProfileUpdateInfo: {
          lastAccountReviewOnUtc: currentDate,
        },
      },
    });
  };

  const handleClose = useCallback((): void => {
    setShowPrompt(false);
  }, []);

  const navigateMyActivity = useCallback(
    async (path: string): Promise<void> => {
      await handleAcknowledge();
      handleClose();
      navigate(path, {
        state: { previousLocation: location },
      });
    },
    [handleClose, navigate, location]
  );

  return (
    <Prompt
      PrimaryButton={{ text: t('account-financial-limit-review.button'), onClick: handleAcknowledge }}
      show={showPrompt}
      title={t('account-review-title')}
      onClose={handleClose}
    >
      <div className="account-review-content">
        <p className="account-review__text">{t('account-review-paragraph1')}</p>

        <p className="account-review__text">{t('account-review-paragraph2')}</p>

        <p className="account-review__text">{t('account-review-paragraph3')}</p>

        <p className="account-review__link">
          <Button
            type="button"
            variant="text"
            onClick={(): void => {
              navigateMyActivity(APP_URLS.myAccount.activity);
            }}
          >
            {t('account-review.prompt.link.1')}
          </Button>
        </p>

        <p className="account-review__link">
          <Button
            type="button"
            variant="text"
            onClick={(): void => {
              navigateMyActivity(`${APP_URLS.myAccount.gamblingControls}?edit=account-review`);
            }}
          >
            {t('account-review.prompt.link.2')}
          </Button>
        </p>
      </div>
    </Prompt>
  );
};
